import { decamelizeKeys } from "humps";
import moment from "moment";
import { utc } from "PFCore/helpers/date";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useScrollLeft } from "PFCore/helpers/use_scroll";
import { useWindowSize } from "PFCore/helpers/use_window_size";
import { useEffect, useMemo, useRef, useState } from "react";

import { Clash, HorizontalLines } from "./components";
import css from "./gantt.module.scss";
import { GanttBar, GanttClash, GanttProps, GanttRowItem, GetBarsFromItem } from "./gantt.types";
import { Arrows, Bar, DayStrips, Header, TooltipContent, Zoom } from "./parts";
import { getClashesFromBars, getDaysDifference } from "./utils/gantt.utils";
import markRows from "./utils/mark_rows";

const getAvailabilityBarsFromItem: GetBarsFromItem = (
  item,
  jobCodeDisplayAs,
  utmSource,
  startOfAxisMoment,
  endOfAxisMoment
) => {
  const list: GanttBar[] = [];
  const profile = (item as unknown as { profile?: unknown }).profile ?? item;
  const { availability } = profile as { availability?: { bookings?: unknown[] } };

  if (!(availability && availability.bookings && availability.bookings.length > 0)) {
    return list;
  }
  return (availability.bookings as any[]).reduce<GanttBar[]>((result, bp, i) => {
    const start = getAttribute(bp, "startDate");
    const end = getAttribute(bp, "endDate");
    if (new Date(end) <= startOfAxisMoment.toDate() || new Date(start) >= endOfAxisMoment.toDate()) {
      return result;
    }

    const newBar: GanttBar = {
      kind: bp.kind,
      itemId: (item as { id: number }).id,
      key: `${(item as { id: number }).id} ${i}`,
      bookingCategoryId: getAttribute(bp, "bookingCategoryId"),
      start,
      end,
      startTime: moment(),
      endTime: moment(),
      load: bp.load,
      tooltipContent: (
        <TooltipContent
          // TODO: [PROF-5099] remove after profile is camelized across the app
          booking={decamelizeKeys(bp) as any}
          bookingCategories={[]}
          jobCodeDisplayAs={jobCodeDisplayAs ?? ""}
          utmSource={utmSource}
        />
      ),
      top: 0,
      left: 0,
      width: 0,
      row: 0
    };

    return [...result, newBar];
  }, []);
};

export const Gantt = <T extends GanttRowItem = GanttRowItem>({
  rowItems,
  rowItemLabelComponent,
  getBarsFromItem = getAvailabilityBarsFromItem as GetBarsFromItem<T>,
  startDate,
  availabilityInfo,
  infoPanel,
  monthsLimit,
  matchMonthsLimit,
  showToday,
  showClashes,
  getClashesFromRowItem,
  jobCodeDisplayAs,
  utmSource,
  style
}: GanttProps<T>) => {
  const { windowWidth } = useWindowSize();
  const [viewportHeight, setViewportHeight] = useState(0);
  const [bars, setBars] = useState<GanttBar[]>([]);
  const [clashes, setClashes] = useState<GanttClash[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [dayWidth, setDayWidth] = useState(15);

  const viewportRef = useRef<HTMLDivElement | null>(null); // for scrolling
  const peoplesElRef = useRef<HTMLDivElement | null>(null); // for viewport height calculation
  const viewportScrollLeft = useScrollLeft(viewportRef.current);

  const startOfAxis = useMemo(() => utc(startDate).startOf("day"), [startDate]);
  const endOfAxis = useMemo(
    () => moment(startOfAxis).add(monthsLimit, "months").endOf("month"),
    [startOfAxis, monthsLimit]
  );

  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);

  useEffect(() => {
    let newBars: GanttBar[] = [];
    const barsByItemId: Record<number, GanttBar[]> = {};
    let newClashes: GanttClash[] = [];

    rowItems.forEach((rowItem) => {
      newBars = newBars.concat(
        getBarsFromItem(rowItem, jobCodeDisplayAs, utmSource, startOfAxis, endOfAxis) || []
      );
    });

    // bars position
    newBars.forEach((bar) => {
      bar.startTime = moment(utc(bar.start)).startOf("day");
      bar.endTime = moment(utc(bar.end)).endOf("day");

      const timeDiff = bar.endTime.diff(bar.startTime, "days") + 1;
      const leftDiff = bar.startTime.diff(startOfAxis, "days");

      bar.top = 0; // update later because you need to calculate overlaps first
      bar.left = leftDiff * dayWidth;
      bar.width = timeDiff * dayWidth;

      barsByItemId[bar.itemId] = barsByItemId[bar.itemId] || [];
      barsByItemId[bar.itemId].push(bar);
    });

    // OVERLAPS
    const newRowHeights: number[] = [];

    const INIT_TOP = 20;
    let _top = INIT_TOP;
    rowItems.forEach((rowItem) => {
      const bars = barsByItemId[rowItem.id as number] || [];
      const maxRowIndex = markRows(bars);
      bars.forEach((bar) => (bar.top = _top + bar.row * 30));
      const rowHeight = 20 + (maxRowIndex + 1) * 30 + 20;
      newRowHeights.push(rowHeight);
      _top += rowHeight;
    });

    const el = viewportRef.current;
    const scrollbarsSize = el ? el.offsetHeight - el.clientHeight : 0;
    const peopleHeight = peoplesElRef.current ? peoplesElRef.current.offsetHeight : 100;
    const headerHeight = 77; // constant so no need to read from DOM

    setViewportHeight(peopleHeight + headerHeight + scrollbarsSize);
    setRowHeights(newRowHeights);
    setBars(newBars);

    // clashes position
    if (showClashes) {
      rowItems.forEach((rowItem, index) => {
        const bars = barsByItemId[rowItem.id as number] || [];
        const rowClashes = getClashesFromBars(bars).concat(
          getClashesFromRowItem ? getClashesFromRowItem(rowItem) : []
        );
        const clashTop = newRowHeights.reduce((acc, row, i) => (i < index ? acc + row : acc), 0);
        const startOfAxisDate = startOfAxis.toDate();
        rowClashes.forEach((clash) => {
          clash.top = clashTop;

          const timeDiff = Math.floor(getDaysDifference(clash.start, clash.end)) + 1;
          const leftDiff = Math.floor(getDaysDifference(startOfAxisDate, clash.start));

          clash.left = leftDiff * dayWidth;
          clash.width = timeDiff * dayWidth;
        });

        newClashes = newClashes.concat(rowClashes);
      });
      setClashes(newClashes);
    }
  }, [windowWidth, JSON.stringify(rowItems), startDate, dayWidth, forceUpdateCounter]);

  // this is a hack but helps with height mismatch between panel and viewport resonably well
  useEffect(() => {
    window.setTimeout(() => setForceUpdateCounter(forceUpdateCounter + 1), 100);
  }, [windowWidth, rowItems, startDate, dayWidth]);

  const scrollWidth = useMemo(() => {
    const numOfDaysShown = endOfAxis.diff(startOfAxis, "days") + 1;
    return numOfDaysShown * dayWidth;
  }, [startOfAxis, endOfAxis, dayWidth]);

  return (
    <div className={css.root} style={style}>
      <HorizontalLines rowHeights={rowHeights} />
      <Arrows scrollEl={viewportRef.current} scrollLeft={viewportScrollLeft} />
      <Zoom dayWidth={dayWidth} setDayWidth={setDayWidth} />

      <div className={css.panel}>
        <div className={css.infoPanel}>{infoPanel}</div>

        <div className={css.people} ref={peoplesElRef}>
          {rowItems.map((rowItem, i) => (
            <div key={`${rowItem.id},${i}`} className={css.label} style={{ height: rowHeights[i] }}>
              {rowItemLabelComponent(rowItem)}
            </div>
          ))}
        </div>
      </div>

      <div className={css.viewport} ref={viewportRef} style={{ height: viewportHeight }}>
        <div className={css.viewportInner} style={{ width: scrollWidth }}>
          <div className={css.calendarHeader}>
            <Header startDate={startDate} dayWidth={dayWidth} calculatedMonthsLimit={monthsLimit} />
          </div>
          <DayStrips
            dayWidth={dayWidth}
            startDate={startDate}
            availabilityInfo={availabilityInfo}
            calculatedMonthsLimit={monthsLimit}
            matchMonthsLimit={matchMonthsLimit}
            showToday={showToday}
          />
          <div className={css.calendarBars}>
            {bars.map((bar) => (
              <Bar {...bar} key={bar.key} dayWidth={dayWidth} viewportRef={viewportRef} />
            ))}
          </div>
          {showClashes && (
            <div className={css.clashes}>
              {clashes.map((clash) => (
                <Clash {...clash} key={clash.key} dayWidth={dayWidth} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
